import { useState, useRef, useEffect } from 'react'
import { Minus, Plus, Trash2, ChevronRight } from 'lucide-react'
import type { CartItem as CartItemType } from '@/types'
import { formatPrice } from '@/lib/formatPrice'

interface CartItemProps {
  item: CartItemType
  language: 'fr' | 'ar' | 'en'
  onQuantityChange: (quantity: number) => void
  onRemove: () => void
}

export function CartItem({
  item,
  language,
  onQuantityChange,
  onRemove
}: CartItemProps) {
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false)
  const [isSwiped, setIsSwiped] = useState(false)
  const [touchStart, setTouchStart] = useState(0)
  const [touchCurrent, setTouchCurrent] = useState(0)
  const itemRef = useRef<HTMLDivElement>(null)

  // Auto-hide remove confirmation after 2 seconds
  useEffect(() => {
    if (showRemoveConfirm) {
      const timer = setTimeout(() => setShowRemoveConfirm(false), 2000)
      return () => clearTimeout(timer)
    }
  }, [showRemoveConfirm])

  // Handle swipe gestures
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.touches[0].clientX)
    setTouchCurrent(e.touches[0].clientX)
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchCurrent(e.touches[0].clientX)
    const diff = touchStart - e.touches[0].clientX

    // Swipe left to reveal delete button (for LTR)
    // Swipe right to reveal delete button (for RTL)
    const isRTL = language === 'ar'
    const swipeThreshold = 50

    if (isRTL) {
      setIsSwiped(diff < -swipeThreshold)
    } else {
      setIsSwiped(diff > swipeThreshold)
    }
  }

  const handleTouchEnd = () => {
    // If swiped far enough, keep showing delete button
    const diff = touchStart - touchCurrent
    const isRTL = language === 'ar'
    const swipeThreshold = 80

    if ((isRTL && diff < -swipeThreshold) || (!isRTL && diff > swipeThreshold)) {
      setIsSwiped(true)
    } else {
      setIsSwiped(false)
    }
  }

  const handleRemove = () => {
    if (showRemoveConfirm) {
      onRemove()
      setShowRemoveConfirm(false)
      setIsSwiped(false)
    } else {
      setShowRemoveConfirm(true)
    }
  }

  const handleQuantityChange = (newQuantity: number) => {
    if (newQuantity >= 1 && newQuantity <= 20) {
      onQuantityChange(newQuantity)
    }
  }

  // Build item description with variant and modifiers
  const buildItemDescription = (): string => {
    const parts: string[] = []

    if (item.variant) {
      parts.push(item.variant.name)
    }

    if (item.modifiers && item.modifiers.length > 0) {
      item.modifiers.forEach((mod) => {
        parts.push(mod.name)
      })
    }

    return parts.join(' • ')
  }

  const itemDescription = buildItemDescription()

  return (
    <div
      ref={itemRef}
      className={`
        relative overflow-hidden rounded-xl
        transition-all duration-200
        ${showRemoveConfirm ? 'border-2 border-red-500 bg-red-50/50' : 'bg-secondary/10'}
      `}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Main Item */}
      <div
        className={`
          flex items-start gap-3 p-3
          transition-transform duration-200 ease-out
          ${isSwiped ? (language === 'ar' ? '-translate-x-16' : 'translate-x-16') : 'translate-x-0'}
        `}
        dir={language === 'ar' ? 'rtl' : 'ltr'}
      >
        {/* Remove Button */}
        <button
          onClick={handleRemove}
          className={`
            flex-shrink-0 p-2.5 rounded-xl
            transition-all duration-200 touch-feedback
            ${showRemoveConfirm
              ? 'bg-red-500 text-white scale-110'
              : 'hover:bg-red-100 text-red-500 hover:scale-105'
            }
          `}
          aria-label={showRemoveConfirm ? 'Confirm remove' : 'Remove item'}
        >
          {showRemoveConfirm ? (
            <Trash2 size={18} fill="currentColor" />
          ) : (
            <Trash2 size={18} />
          )}
        </button>

        {/* Item Details */}
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-primary text-sm mb-1 line-clamp-2">
            {item.name}
          </h4>

          {/* Variant and Modifiers */}
          {itemDescription && (
            <div className="text-xs text-muted-foreground mb-2 line-clamp-1">
              {itemDescription}
            </div>
          )}

          {/* Variant/Modifier Prices Breakdown */}
          {(item.variant || (item.modifiers && item.modifiers.length > 0)) && (
            <div className="text-xs text-muted-foreground/70 mb-2 space-y-0.5">
              {item.variant && item.variant.price > 0 && (
                <div className="flex items-center gap-1">
                  <ChevronRight size={10} />
                  <span>{item.variant.name}: +{formatPrice(item.variant.price)}</span>
                </div>
              )}
              {item.modifiers?.map((mod, idx) => (
                mod.price > 0 && (
                  <div key={idx} className="flex items-center gap-1">
                    <ChevronRight size={10} />
                    <span>{mod.name}: +{formatPrice(mod.price)}</span>
                  </div>
                )
              ))}
            </div>
          )}

          {/* Price and Quantity */}
          <div className="flex items-center justify-between gap-3">
            <div className="flex flex-col">
              <span className="font-bold text-primary text-base">
                {formatPrice(item.price * item.quantity)}
              </span>
              <span className="text-xs text-muted-foreground">
                {formatPrice(item.price)} × {item.quantity}
              </span>
            </div>

            {/* Quantity Controls */}
            <div className="flex items-center gap-1 bg-white rounded-lg p-1 shadow-sm">
              <button
                onClick={() => handleQuantityChange(item.quantity - 1)}
                disabled={item.quantity <= 1}
                className={`
                  w-8 h-8 flex items-center justify-center
                  rounded-lg transition-all touch-feedback
                  ${item.quantity > 1
                    ? 'hover:bg-primary/10 text-primary active:scale-90'
                    : 'text-gray-300 cursor-not-allowed'
                  }
                `}
                aria-label="Decrease quantity"
              >
                <Minus size={16} />
              </button>

              <span className="w-8 text-center font-semibold text-primary">
                {item.quantity}
              </span>

              <button
                onClick={() => handleQuantityChange(item.quantity + 1)}
                disabled={item.quantity >= 20}
                className={`
                  w-8 h-8 flex items-center justify-center
                  rounded-lg hover:bg-primary/10 text-primary
                  transition-all touch-feedback
                  ${item.quantity < 20 ? 'active:scale-90' : 'text-gray-300 cursor-not-allowed'}
                `}
                aria-label="Increase quantity"
              >
                <Plus size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Swipe Action Indicator */}
      {isSwiped && !showRemoveConfirm && (
        <div
          className={`
            absolute inset-y-0 flex items-center justify-center
            bg-red-500 text-white
            ${language === 'ar' ? 'left-0 rounded-l-xl' : 'right-0 rounded-r-xl'}
            w-16
          `}
        >
          <Trash2 size={20} />
        </div>
      )}

      {/* Confirmation Overlay */}
      {showRemoveConfirm && (
        <div
          className={`
            absolute inset-0 bg-red-500/95
            flex items-center justify-center
            rounded-xl
            animate-fade-in
          `}
        >
          <div className="text-center text-white">
            <Trash2 size={24} className="mx-auto mb-1" fill="currentColor" />
            <p className="text-xs font-medium">
              {language === 'fr' && 'Confirmer'}
              {language === 'ar' && 'تأكيد'}
              {language === 'en' && 'Confirm'}
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
